// path: frontend/src/hooks/useDb.js
/**
Хуки для получения данных из API с кэшированием React Query.
useTasksDb принимает опциональный параметр showAll,
чтобы корректно формировать queryKey для useProjectsQuery
и обеспечивать re-fetch при переключении тумблера "Все проекты".
*/
import { useMemo } from "react";
import { useEmployeesQuery } from "../api/queries/employees";
import { useDepartmentsQuery } from "../api/queries/departments";
import { useKbsQuery } from "../api/queries/kbs";
import { useTasksQuery } from "../api/queries/tasks";
import { useProjectsQuery } from "../api/queries/projects";
import { useVacationsQuery } from "../api/queries/vacations";
import { useChangeRequestsQuery } from "../api/queries/change-requests";
import { useRoleDelegationsQuery } from "../api/queries/role-delegations";
import { useAssignmentApprovalsQuery } from "../api/queries/assignment-approvals";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";

const MAX_REF_LIMIT = 5000;

const unwrapTasks = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  return [];
};

const unwrapProjects = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  return [];
};

export const useTasksDb = (options = {}) => {
  const { showAll = false } = options;
  const employeesData = useEmployeesQuery().data;
  const departmentsData = useDepartmentsQuery().data;
  const tasksRes = useTasksQuery({ limit: MAX_REF_LIMIT }).data;
  const projectsRes = useProjectsQuery({ limit: MAX_REF_LIMIT, showAll }).data;

  return useMemo(
    () => ({
      tasks: unwrapTasks(tasksRes),
      projects: unwrapProjects(projectsRes),
      employees: employeesData ?? [],
      departments: departmentsData ?? [],
    }),
    [tasksRes, projectsRes, employeesData, departmentsData],
  );
};

/**
Данные для архивной вкладки. Задачи — только архивные
(archivedOnly=true, чтобы не тащить активные). Проекты —
includeArchived=true: нужен полный справочник, чтобы подсветить код
проекта у задач, закрытых вручную (проект ещё активен).
*/
export const useArchiveDb = () => {
  const employeesData = useEmployeesQuery().data;
  const departmentsData = useDepartmentsQuery().data;
  const tasksRes = useTasksQuery({
    limit: MAX_REF_LIMIT,
    archivedOnly: true,
  }).data;
  const projectsRes = useProjectsQuery({
    limit: MAX_REF_LIMIT,
    includeArchived: true,
  }).data;

  return useMemo(
    () => ({
      tasks: unwrapTasks(tasksRes),
      projects: unwrapProjects(projectsRes),
      employees: employeesData ?? [],
      departments: departmentsData ?? [],
    }),
    [tasksRes, projectsRes, employeesData, departmentsData],
  );
};

export const useTasksViewDb = useTasksDb;
export const useScopeDb = useTasksDb;
export const useScheduleDb = useTasksDb;

export const useStaffDb = () => {
  const employeesData = useEmployeesQuery().data;
  const departmentsData = useDepartmentsQuery().data;
  const kbsData = useKbsQuery().data;
  const vacationsData = useVacationsQuery().data;
  const tasksRes = useTasksQuery({ limit: MAX_REF_LIMIT }).data;

  return useMemo(
    () => ({
      employees: employeesData ?? [],
      departments: departmentsData ?? [],
      kbs: kbsData ?? [],
      vacations: vacationsData ?? [],
      tasks: unwrapTasks(tasksRes),
    }),
    [employeesData, departmentsData, kbsData, vacationsData, tasksRes],
  );
};

export const useReportsDb = () => {
  const employeesData = useEmployeesQuery().data;
  const departmentsData = useDepartmentsQuery().data;
  const kbsData = useKbsQuery().data;
  const tasksRes = useTasksQuery({ limit: MAX_REF_LIMIT }).data;
  const projectsRes = useProjectsQuery({ limit: MAX_REF_LIMIT }).data;
  const vacationsData = useVacationsQuery().data;

  return useMemo(
    () => ({
      tasks: unwrapTasks(tasksRes),
      projects: unwrapProjects(projectsRes),
      employees: employeesData ?? [],
      departments: departmentsData ?? [],
      kbs: kbsData ?? [],
      vacations: vacationsData ?? [],
    }),
    [
      tasksRes,
      projectsRes,
      employeesData,
      departmentsData,
      kbsData,
      vacationsData,
    ],
  );
};

export const useWorkloadDb = () => {
  const employeesData = useEmployeesQuery().data;
  const departmentsData = useDepartmentsQuery().data;
  const kbsData = useKbsQuery().data;
  const projectsRes = useProjectsQuery({ limit: MAX_REF_LIMIT }).data;
  const tasksRes = useTasksQuery({ limit: MAX_REF_LIMIT }).data;

  return useMemo(
    () => ({
      employees: employeesData ?? [],
      departments: departmentsData ?? [],
      kbs: kbsData ?? [],
      projects: unwrapProjects(projectsRes),
      tasks: unwrapTasks(tasksRes),
    }),
    [employeesData, departmentsData, kbsData, projectsRes, tasksRes],
  );
};

export const useRequestsDb = () => {
  const employeesData = useEmployeesQuery().data;
  const departmentsData = useDepartmentsQuery().data;
  const changeRequestsData = useChangeRequestsQuery().data;
  const vacationsData = useVacationsQuery().data;
  const roleDelegationsData = useRoleDelegationsQuery().data;
  const tasksRes = useTasksQuery({ limit: MAX_REF_LIMIT }).data;
  const projectsRes = useProjectsQuery({ limit: MAX_REF_LIMIT }).data;
  const assignmentApprovalsData = useAssignmentApprovalsQuery().data;

  return useMemo(
    () => ({
      changeRequests: changeRequestsData ?? [],
      vacations: vacationsData ?? [],
      roleDelegations: roleDelegationsData ?? [],
      tasks: unwrapTasks(tasksRes),
      projects: unwrapProjects(projectsRes),
      employees: employeesData ?? [],
      departments: departmentsData ?? [],
      assignmentApprovals: assignmentApprovalsData ?? [],
    }),
    [
      changeRequestsData,
      vacationsData,
      roleDelegationsData,
      tasksRes,
      projectsRes,
      employeesData,
      departmentsData,
      assignmentApprovalsData,
    ],
  );
};

export const useJournalDb = () => {
  const employeesData = useEmployeesQuery().data;
  const { data: auditData } = useQuery({
    queryKey: ["audit"],
    queryFn: () => api.get("/audit"),
  });

  return useMemo(
    () => ({ audit: auditData ?? [], employees: employeesData ?? [] }),
    [auditData, employeesData],
  );
};

export const useCabinetDb = () => {
  const employeesData = useEmployeesQuery().data;
  const departmentsData = useDepartmentsQuery().data;
  const kbsData = useKbsQuery().data;
  const tasksRes = useTasksQuery({ limit: MAX_REF_LIMIT }).data;
  const projectsRes = useProjectsQuery({ limit: MAX_REF_LIMIT }).data;
  const vacationsData = useVacationsQuery().data;
  const roleDelegationsData = useRoleDelegationsQuery().data;

  return useMemo(
    () => ({
      tasks: unwrapTasks(tasksRes),
      projects: unwrapProjects(projectsRes),
      employees: employeesData ?? [],
      departments: departmentsData ?? [],
      kbs: kbsData ?? [],
      vacations: vacationsData ?? [],
      roleDelegations: roleDelegationsData ?? [],
    }),
    [
      tasksRes,
      projectsRes,
      employeesData,
      departmentsData,
      kbsData,
      vacationsData,
      roleDelegationsData,
    ],
  );
};
